import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation } from '@/api/query'
import { useBrand } from '@/brand/context'
import { GOOGLE_DOCS } from '@/lib/docs'
import { Button } from '@/ui/Button'
import { SetupGuide } from '@/ui/SetupGuide'
import { InlineError } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { WizardChrome, WizardFooter } from './WizardLayout'
import { STEP_META, connectionFor, detailOf, pathFor, stepStatus } from './steps'

type Recovery = Schema<'RecoveryResponse'>

const PROVIDER = 'google_search_console'

/**
 * Step 2 — S-D2's `s2` and `s2-fail`.
 *
 * The scope is named **before** the button, not after: people decline OAuth when
 * they cannot tell what is being asked for, and "read-only" is the whole
 * reassurance here. The bullets name **one permission per scope in
 * `oauth.SCOPES`**, so widening that tuple later means changing this copy in the
 * same commit.
 *
 * **`siteverification` was missing from the list until `GOOG3` (2026-08-08)**, and
 * that is the second time this screen has been out of step with the consent screen
 * behind its own button. The first was *"we never read: Analytics"* while
 * `analytics.readonly` sat registered with no code behind it (`GOOG1`); this one
 * was quieter and had the same shape — the list said *"anything outside those
 * two"* while Google's consent screen renders **three** tick boxes. Shot 3 of
 * `docs/GOOGLE-OAUTH-DEMO-SHOOT.md` is this frame and Shot 4 is that consent
 * screen, so a reviewer meets both within forty seconds: §8's rejection cause 1
 * reached by way of the app's own copy rather than by a missing demonstration.
 *
 * `no_access` is its own screen, not a variant of the failure card, because the
 * recovery is genuinely different: the property exists and this Google account
 * cannot see it, which is usually somebody's agency owning it. All three routes
 * S-D2 drew are here — try a different account (press Connect again, which sends
 * a fresh consent), have us draft the email, or verify by DNS.
 */
export function SearchConsoleStep(): JSX.Element {
  const brand = useBrand()
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const connection = connectionFor(state, PROVIDER)
  const step = stepStatus(state, 'search_console')
  const [recovery, setRecovery] = useState<Recovery | null>(null)
  // ONB-7. **Which** route was pressed, not merely that one was. S-D2 draws
  // routes 2 and 3 as two distinct actions — draft the email, show the DNS
  // record — and both buttons opened the same panel showing both answers, so
  // pressing *Show record* handed back a paragraph of email copy above the
  // thing that was asked for. One fetch still serves both (the route composes
  // both halves in one read); what differs is what is then revealed.
  const [route, setRoute] = useState<'email' | 'record' | null>(null)

  const connect = useMutation(() =>
    call(() => api.POST('/api/v1/onboarding/search-console/connect', {})),
  )
  const loadRecovery = useMutation(() =>
    call(() => api.GET('/api/v1/onboarding/search-console/recovery', {})),
  )

  async function onConnect(): Promise<void> {
    setRecovery(null)
    const result = await connect.run(undefined)
    if (!result.ok) return
    adopt(result.data.state)
    if (result.data.redirect_url) {
      // A full navigation: this is Google's consent screen, and `fetch` would
      // hand it back as an opaque body nobody can render.
      window.location.assign(result.data.redirect_url)
      return
    }
    if (result.data.status === 'connected') void navigate(pathFor(result.data.state.current_step))
  }

  async function onRecovery(which: 'email' | 'record'): Promise<void> {
    setRoute(which)
    const result = await loadRecovery.run(undefined)
    if (result.ok) setRecovery(result.data)
  }

  const noAccess = connection?.status === 'no_access'
  const listed = detailOf(connection).properties
  const properties = Array.isArray(listed) ? (listed as string[]) : []

  return (
    <div className="app-wiz">
      {/* ONB-32. S-D2's `s2-fail` keeps the step rail and drops the meter.
          A bar reporting how far along setup is, directly above a card
          saying this step could not be completed, is two answers to one
          question — and the rail already carries the `error` dot that says
          where the tenant is. */}
      <WizardChrome current="search_console" meter={!noAccess} />

      <section className="ar-card">
        <div className="ar-card__body ar-stack">
          {/* Parity P-ONB2-01: the heading sits in the body with its intro,
              as step 1 draws it and `onboarding.html#s2` does — no header
              rule. The no-access screen keeps its own heading here too. */}
          <div>
            <h2 className="ar-h2">
              {noAccess
                ? `${state.site?.domain ?? 'Your site'} isn't in this Google account`
                : STEP_META.search_console.title}
            </h2>
            {noAccess ? null : (
              <p className="ar-muted" style={{ margin: 'var(--ar-space-2) 0 0' }}>
                This is where your real ranking and click data comes from. Without it we can still
                audit and write, but the dashboard will be mostly empty.
              </p>
            )}
          </div>
          <InlineError failure={connect.state.failure ?? loadRecovery.state.failure} />

          {noAccess ? (
            <>
              <div className="ar-alert ar-alert--warning" role="alert">
                {/* ONB-34. `⚠` on a warning, settled for the product by SF-16's
                    AUTH-36: `✕` refuses what was typed, `⚠` cautions about what
                    is about to happen, and `!` stands in for a whole screen that
                    could not be drawn. Three situations, three glyphs. */}
                <span className="ar-alert__icon" aria-hidden="true">
                  ⚠
                </span>
                <div className="ar-alert__body">
                  <strong>We connected, but found no matching property</strong>
                  {connection.account_label ? (
                    <>
                      You signed in as <strong>{connection.account_label}</strong>
                      {properties.length > 0
                        ? `, which has ${String(properties.length)} ${
                            properties.length === 1 ? 'property' : 'properties'
                          } — none of them is this site.`
                        : ', which owns no matching property.'}
                    </>
                  ) : (
                    'That Google account owns no property matching this site.'
                  )}
                </div>
              </div>

              {properties.length > 0 ? (
                <ul className="ar-muted" style={{ margin: 0 }}>
                  {properties.map((property) => (
                    <li key={property}>{property}</li>
                  ))}
                </ul>
              ) : null}

              <div className="ar-stack ar-stack--tight">
                <strong>Three ways forward</strong>

                <div className="app-conn">
                  <span className="app-conn__logo" aria-hidden="true">
                    1
                  </span>
                  <span className="app-conn__body">
                    <strong>Sign in with a different Google account</strong>
                    <span>If you have another account that owns the property.</span>
                  </span>
                  <Button
                    size="sm"
                    busy={connect.pending}
                    busyLabel="Opening Google"
                    onClick={() => {
                      void onConnect()
                    }}
                  >
                    Switch account
                  </Button>
                </div>

                <div className="app-conn">
                  <span className="app-conn__logo" aria-hidden="true">
                    2
                  </span>
                  <span className="app-conn__body">
                    <strong>Ask whoever manages your site</strong>
                    <span>We&rsquo;ll draft the email — they just add you in Search Console.</span>
                  </span>
                  <Button
                    size="sm"
                    busy={loadRecovery.pending && route === 'email'}
                    busyLabel="Drafting"
                    onClick={() => {
                      void onRecovery('email')
                    }}
                  >
                    Draft email
                  </Button>
                </div>

                <div className="app-conn">
                  <span className="app-conn__logo" aria-hidden="true">
                    3
                  </span>
                  <span className="app-conn__body">
                    <strong>Verify the site yourself</strong>
                    <span>Add a DNS TXT record. We&rsquo;ll show the exact value to paste.</span>
                  </span>
                  <Button
                    size="sm"
                    busy={loadRecovery.pending && route === 'record'}
                    busyLabel="Fetching"
                    onClick={() => {
                      void onRecovery('record')
                    }}
                  >
                    Show record
                  </Button>
                </div>
              </div>

              {recovery && route ? <RecoveryDetail recovery={recovery} route={route} /> : null}

              <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
                Not sure which of the three applies?{' '}
                <a
                  className="ar-link"
                  href={GOOGLE_DOCS.noMatchingProperty}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  What &ldquo;no matching property&rdquo; means, and what to do ↗
                </a>
              </p>
            </>
          ) : (
            <>
              {/* What has to be true *before* Connect will work, none of which
                  is decided in AugRank. Shown above the button rather than in
                  the failure card after it: the tenant who signs in with the
                  wrong Google account finds out here instead of one round trip
                  through Google later. Hidden once connected — the card below
                  says what it reached. */}
              {connection?.status === 'connected' ? null : (
                <SetupGuide
                  steps={[
                    <>
                      Google will only let {brand.productName} read a site that the account you sign
                      in with <strong>already has in Search Console</strong>. Check at
                      search.google.com/search-console first, and note which Google account lists{' '}
                      {state.site?.domain ?? 'your site'}.
                    </>,
                    <>
                      If someone else set it up — a developer, an agency — ask them to add your
                      Google account there under <em>Users and permissions</em>. Any level works.
                    </>,
                    <>
                      If nobody has, you can verify it yourself with one DNS record. If Connect
                      finds no matching site, this step shows the exact record to add.
                    </>,
                  ]}
                  href={GOOGLE_DOCS.beforeYouStart}
                  linkText="How to connect Google, step by step"
                />
              )}

              <div className="app-conn">
                <span className="app-conn__logo" aria-hidden="true">
                  G
                </span>
                <span className="app-conn__body">
                  <strong>Google Search Console</strong>
                  <span>
                    Read-only. We never post, change, or delete anything in your Google account.
                  </span>
                </span>
                <Button
                  tone="primary"
                  busy={connect.pending}
                  busyLabel="Opening Google"
                  onClick={() => {
                    void onConnect()
                  }}
                >
                  {connection?.status === 'connected' ? 'Reconnect' : 'Connect'}
                </Button>
              </div>

              <ul
                className="ar-muted"
                style={{
                  fontSize: 'var(--ar-text-body-sm-size)',
                  margin: 0,
                  paddingLeft: 'var(--ar-space-5)',
                }}
              >
                {/* Parity P-ONB2-02, with one deliberate departure: the
                    mockup's three bullets, worded as drawn — except that the
                    grant this button asks for *does* include Analytics and the
                    ownership record, so "We never read: Analytics" would be a
                    false consent statement. The mockup predates GA4 (S-B33).
                    The extra scopes are named in one bullet each, and the
                    "never" bullet names what is actually never read. */}
                <li>
                  We read: impressions, clicks, average position, and your verified property list.
                </li>
                <li>
                  We also read from Google Analytics, if you allow it: sessions, users and key
                  events. Read-only, and everything else works without it.
                </li>
                <li>
                  If your site isn&rsquo;t in Search Console yet, we fetch the DNS entry that proves
                  it&rsquo;s yours and show it to you — Google does the verifying.
                </li>
                <li>We never read: Ads, Gmail, Drive, or anything outside those.</li>
                <li>You can disconnect at any time from Settings → Connections.</li>
              </ul>

              {connection?.status === 'connected' ? (
                <div className="ar-alert ar-alert--success" role="status">
                  <span className="ar-alert__icon" aria-hidden="true">
                    ✓
                  </span>
                  <div className="ar-alert__body">
                    <strong>Connected</strong>
                    {connection.account_label ? `Signed in as ${connection.account_label}.` : null}
                  </div>
                </div>
              ) : step?.status === 'failed' && detailOf(step).message ? (
                <div className="ar-alert ar-alert--danger" role="alert">
                  {/* ONB-35. `✕` — this is a refusal, not a caution (ONB-34). */}
                  <span className="ar-alert__icon" aria-hidden="true">
                    ✕
                  </span>
                  <div className="ar-alert__body">
                    <strong>We could not connect Search Console</strong>
                    {String(detailOf(step).message)}
                    {/* ONB-5. S-D2's declined-consent copy is explicit about the
                        two ways out — *"we say so plainly, keep the wizard where
                        it is, and offer Try again or Skip"*. The card said so
                        plainly and then offered neither: the only retry was the
                        connector row above, still reading *Connect*, which is
                        the label for something that has not been attempted. A
                        person who has just been told the attempt failed is
                        looking for the word *again*. */}
                    <div className="ar-btn-group" style={{ marginTop: 'var(--ar-space-3)' }}>
                      <Button
                        size="sm"
                        busy={connect.pending}
                        busyLabel="Opening Google"
                        onClick={() => {
                          void onConnect()
                        }}
                      >
                        Try again
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* ONB-4. **No Continue on `no_access`.** The three routes above are
            the ways forward from this screen, and a primary button beside them
            offering a fourth — carry on regardless — makes the recovery look
            optional when the step is not done and nothing has been connected.
            S-D2 draws Back and Skip alone here; skipping is the honest way past,
            and it says what is lost. */}
        <WizardFooter current="search_console">
          {noAccess ? null : (
            <Button
              tone="primary"
              onClick={() => {
                const next = state.steps.find((row) => row.step === 'linkedin')
                void navigate(pathFor(next?.step ?? 'linkedin'))
              }}
            >
              Continue
            </Button>
          )}
        </WizardFooter>
      </section>
    </div>
  )
}

/**
 * Route 2 or route 3 — whichever was pressed (ONB-7).
 *
 * One fetch answers both, because the route composes both halves in a single
 * read and a second call would be a second round trip for a value already in
 * hand. What is *shown* is the one that was asked for: S-D2 draws these as two
 * distinct actions, and revealing both meant the DNS record arrived under a
 * paragraph of email copy nobody had asked to see.
 *
 * The DNS record is absent when Google cannot mint the token — the wizard says
 * so rather than printing a record that would never verify, which is the whole
 * reason `verification_record` is nullable.
 */
function RecoveryDetail({
  recovery,
  route,
}: {
  recovery: Recovery
  route: 'email' | 'record'
}): JSX.Element {
  if (route === 'email') {
    return (
      <div className="ar-panel ar-stack ar-stack--tight">
        <strong>The email to send</strong>
        <p className="ar-muted" style={{ margin: 0, fontSize: 'var(--ar-text-body-sm-size)' }}>
          Subject: {recovery.owner_request_subject}
        </p>
        <pre
          className="ar-mono"
          style={{
            margin: 0,
            whiteSpace: 'pre-wrap',
            fontSize: 'var(--ar-text-caption-size)',
          }}
        >
          {recovery.owner_request_body}
        </pre>
      </div>
    )
  }

  return (
    <div className="ar-panel ar-stack ar-stack--tight">
      <strong>The DNS record</strong>
      {recovery.verification_record ? (
        <p className="ar-mono" style={{ margin: 0, fontSize: 'var(--ar-text-caption-size)' }}>
          {recovery.verification_record.kind} {recovery.verification_record.name} →{' '}
          {recovery.verification_record.value}
        </p>
      ) : (
        <p className="ar-muted" style={{ margin: 0, fontSize: 'var(--ar-text-body-sm-size)' }}>
          Google issues this value and did not, which happens when the site-ownership permission was
          not granted. Try the email above, or reconnect and tick that box.
        </p>
      )}
    </div>
  )
}
